import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Lock, Gift, ChevronRight, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { MONTHS_DATA } from '../../data/curriculum';
import useAuthStore from '../../store/authStore';
import progressService from '../../services/progressService';

export default function Home() {
  const { student, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [gameMap, setGameMap] = useState(null);

  // Fetch game map and progress from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch game map (months/weeks from database)
        try {
          const mapData = await progressService.getGameMap();
          setGameMap(mapData);
        } catch (mapErr) {
          console.error('Failed to fetch game map:', mapErr);
          // Will use fallback MONTHS_DATA
        }

        // Fetch progress if authenticated
        if (isAuthenticated && student) {
          try {
            const data = await progressService.getAllWeeksProgress();
            setProgressData(data);
          } catch (progressErr) {
            console.error('Failed to fetch progress:', progressErr);
            setError('Could not load progress from server.');
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Could not load data from server. Using offline data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, student]);

  // Use database game map or fallback to local MONTHS_DATA
  const monthsData = gameMap?.months?.length > 0
    ? gameMap.months.map(month => ({
        id: month.number,
        name: month.name,
        theme: month.theme,
        weeks: month.weeks.map(week => ({
          number: week.number,
          title: week.title,
          theme: week.theme,
          envDay: week.env_day,
        })),
      }))
    : MONTHS_DATA;

  // Get progress values - from API or defaults
  const currentWeek = progressData?.current_week ?? student?.current_week ?? 1;
  const totalWeeks = gameMap?.total_weeks ?? progressData?.total_weeks ?? 36;
  const completedWeeksCount = progressData?.weeks?.filter(w => w.is_completed).length ?? Math.max(0, currentWeek - 1);

  // Calculate overall progress percentage
  const progressPercent = Math.round((completedWeeksCount / totalWeeks) * 100);

  // Determine week status based on API data or student's current_week
  const getWeekStatus = (weekNumber) => {
    // If we have API data, use it
    if (progressData?.weeks) {
      const weekData = progressData.weeks.find(w => w.week_number === weekNumber);
      if (weekData) {
        if (weekData.is_completed) return 'completed';
        if (weekData.is_unlocked) return 'available';
        return 'locked';
      }
    }

    // Fallback: use student's current_week
    if (weekNumber < currentWeek) return 'completed';
    if (weekNumber === currentWeek) return 'available';
    return 'locked';
  };

  // Get the current week data for display
  const getCurrentWeekData = () => {
    for (const month of monthsData) {
      const week = month.weeks.find((w) => w.number === currentWeek);
      if (week) return { week, month };
    }
    return null;
  };

  const currentWeekData = getCurrentWeekData();

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed':
        return 'game-star game-star-completed';
      case 'available':
        return 'game-star game-star-available';
      default:
        return 'game-star game-star-locked';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Star size={24} className="fill-current" />;
      case 'available':
        return <Star size={24} />;
      default:
        return <Lock size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-leaf-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Error Banner (non-blocking) */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-amber-700 text-sm">{error}</p>
        </motion.div>
      )}

      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-leaf-500 to-leaf-600 rounded-2xl p-6 mb-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-leaf-100 text-sm">Week {currentWeek} of {totalWeeks}</p>
            <h2 className="text-xl text-white font-heading">
              {currentWeekData?.week.title || 'Loading...'}
            </h2>
            <p className="text-leaf-100 text-sm mt-1">Keep going! You're doing great!</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{progressPercent}%</div>
            <div className="text-leaf-200 text-xs">Complete</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-2 bg-leaf-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 1, delay: 0.5 }}
            className="h-full bg-sunshine-400 rounded-full"
          />
        </div>
      </motion.div>

      {/* Level Map */}
      <div className="space-y-8">
        {monthsData.map((month, monthIndex) => (
          <motion.div
            key={month.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: monthIndex * 0.1 }}
          >
            {/* Month Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-leaf-100 flex items-center justify-center">
                <span className="text-leaf-700 font-bold text-sm">{month.id}</span>
              </div>
              <div>
                <h3 className="text-lg font-heading text-leaf-800">{month.name}</h3>
                <p className="text-sm text-gray-500">{month.theme}</p>
              </div>
            </div>

            {/* Weeks Grid */}
            <div className="relative">
              {/* Connection line */}
              <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-leaf-200 -z-10" />

              <div className="space-y-4 pl-4">
                {month.weeks.map((week, weekIndex) => {
                  const status = getWeekStatus(week.number);
                  return (
                    <motion.div
                      key={week.number}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: monthIndex * 0.1 + weekIndex * 0.05 }}
                      className="flex items-center gap-4"
                    >
                      {/* Week Star */}
                      <Link
                        to={status !== 'locked' ? `/app/week/${week.number}` : '#'}
                        onClick={(e) => {
                          if (status === 'locked') {
                            e.preventDefault();
                          }
                        }}
                        className={getStatusStyle(status)}
                      >
                        {getStatusIcon(status)}
                      </Link>

                      {/* Week Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Week {week.number}</span>
                          {week.number === currentWeek && (
                            <span className="badge badge-success text-xs">Current</span>
                          )}
                          {week.envDay && (
                            <span className="badge badge-warning text-xs flex items-center gap-1">
                              <Gift size={12} />
                              {week.envDay.name || week.envDay}
                            </span>
                          )}
                        </div>
                        <p className={`font-medium ${
                          status === 'locked' ? 'text-gray-400' : 'text-gray-800'
                        }`}>
                          {week.title}
                        </p>
                      </div>

                      {/* Arrow for available */}
                      {status === 'available' && (
                        <ChevronRight className="text-leaf-500" size={24} />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Load More / Continue */}
      <div className="mt-8 text-center">
        <p className="text-gray-500 text-sm mb-4">
          Complete Week {currentWeek} to unlock more weeks!
        </p>
        <Link
          to={`/app/week/${currentWeek}`}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Sparkles size={18} />
          Continue Week {currentWeek}
        </Link>
      </div>
    </div>
  );
}
